// Демо-данные окна «Настройки» для главной Hub.
// Слева разделы настроек рабочего пространства, справа их содержимое.
// Каналы намеренно стартуют пустыми — как в оригинале, видно пустое состояние и окно «Добавить канал».
import { t } from "@/lib/i18n";

export type SettingsSection = {
  id: string;
  label: string;
  icon: string;
  text: string;
  /** пункт-ссылка: открывает раздел рабочего пространства вместо вкладки настроек */
  anchor?: string;
};

export const settingsSections: SettingsSection[] = [
  {
    id: "basic",
    label: t("Основные настройки"),
    icon: "ri-settings-3-line",
    text: t("Название, адрес и язык рабочего пространства — то, что видно в письмах и ссылках."),
  },
  {
    id: "general",
    label: t("Общая информация"),
    icon: "ri-information-line",
    text: t("Описание компании, сайт и отрасль — помогают AI-ассистенту отвечать точнее."),
  },
  {
    id: "roles",
    label: t("Настройки ролей пользователей"),
    icon: "ri-shield-user-line",
    text: t("Что могут делать агенты, менеджеры и администраторы в рабочем пространстве."),
  },
  {
    id: "users",
    label: t("Пользователи"),
    icon: "ri-group-line",
    text: t("Участники рабочего пространства: приглашение по почте Google, роли, отзыв доступа, «Посмотреть как»."),
    anchor: "#user-settings",
  },
  {
    id: "fields",
    label: t("Пользовательские настройки полей"),
    icon: "ri-input-field",
    text: t("Свои поля карточки клиента: город, номер договора, сегмент и любые другие."),
  },
  {
    id: "team",
    label: t("Настройки команды"),
    icon: "ri-team-line",
    text: t("Команды группируют сотрудников, чтобы разом назначать им диалоги и контакты."),
  },
  {
    id: "apps",
    label: t("Приложения"),
    icon: "ri-apps-line",
    text: t("Дополнительные приложения рабочего пространства — включайте нужные в один клик."),
  },
  {
    id: "channels",
    label: t("Каналы"),
    icon: "ri-share-forward-line",
    text: t("Управляйте своими каналами обмена сообщениями и открывайте новые, которые помогут вам привлечь больше клиентов."),
  },
  {
    id: "integrations",
    label: t("Интеграции"),
    icon: "ri-links-line",
    text: t("Свяжите Hub с CRM, таблицами и складом — данные будут обновляться синхронно."),
  },
  {
    id: "zoho",
    label: t("Zoho CRM и Books"),
    icon: "ri-refresh-line",
    text: t("Синхронизация с Zoho, история переписки Wazzup и продавцы Sales Person → сотрудники Hub."),
  },
  {
    id: "notifications",
    label: t("Уведомления"),
    icon: "ri-notification-3-line",
    text: t("Куда и о чём присылать уведомления: браузер, почта, Telegram, частота и тихие часы."),
  },
  {
    id: "feedback",
    label: t("Журнал замечаний"),
    icon: "ri-bug-line",
    text: t("Идеи и проблемы пользователей со скриншотами экранов — отсюда робот Claude Code запускает исправления."),
  },
  {
    id: "audit",
    label: t("Журнал действий"),
    icon: "ri-history-line",
    text: t("Входы, добавление пользователей, смена ролей и прав, отзыв доступа — кто и когда."),
  },
];

export const workspaceLanguages = ["Русский", "English", "Español", "Deutsch", "Português"];

export const workspaceZones = [
  { id: "Europe/Rome", label: t("(GMT+01:00) Европа/Рим") },
  { id: "Europe/Moscow", label: t("(GMT+03:00) Европа/Москва") },
  { id: "Europe/London", label: t("(GMT+00:00) Европа/Лондон") },
  { id: "Asia/Almaty", label: t("(GMT+05:00) Азия/Алматы") },
  { id: "Asia/Dubai", label: t("(GMT+04:00) Азия/Дубай") },
  { id: "America/New_York", label: t("(GMT−05:00) Америка/Нью-Йорк") },
  { id: "UTC", label: "(GMT+00:00) UTC" },
];

export const workspaceIndustries = [
  "Услуги для бизнеса",
  "Оптовая торговля",
  "Розничная торговля",
  "Онлайн-школа",
  "Медицина",
  "Недвижимость",
  "Производство",
  "Другое",
];

export const workspaceSizes = ["1–5 сотрудников", "6–10 сотрудников", "11–50 сотрудников", "51–200 сотрудников", "Больше 200"];

/* ── каналы ── */

export type ChannelOption = {
  id: string;
  name: string;
  icon: string;
  category: string;
  hint: string;
};

export const channelOptions: ChannelOption[] = [
  { id: "whatsapp", name: "WhatsApp", icon: "ri-whatsapp-line", category: t("Мессенджер"), hint: t("Обычный аккаунт по QR или номеру, либо официальный WABA") },
  { id: "telegram", name: "Telegram", icon: "ri-telegram-line", category: t("Мессенджер"), hint: t("Аккаунт по QR-коду или бот по токену") },
  { id: "instagram", name: "Instagram Direct", icon: "ri-instagram-line", category: t("Мессенджер"), hint: t("Вход в аккаунт по логину или через бизнес-страницу Facebook") },
  { id: "viber", name: "Viber", icon: "ri-phone-line", category: t("Мессенджер"), hint: t("Аккаунт по QR-коду или официальный Viber Business по токену") },
  { id: "vk", name: "VK Сообщения", icon: "ri-vk-line", category: t("Мессенджер"), hint: t("Сообщения сообщества ВКонтакте") },
  { id: "avito", name: "Avito Сообщения", icon: "ri-store-2-line", category: t("Маркетплейс"), hint: t("Переписка по объявлениям и заявки") },
  { id: "email", name: "Почта", icon: "ri-mail-line", category: t("Почта"), hint: t("Ящик по IMAP и SMTP с историей писем") },
  { id: "chat", name: "Онлайн-чат на сайте", icon: "ri-customer-service-2-line", category: t("Сайт"), hint: t("Виджет на сайте, ответы из Hub") },
  { id: "sms", name: "SMS-шлюз", icon: "ri-message-2-line", category: "SMS", hint: t("Короткие уведомления и подтверждения") },
  { id: "calls", name: "Телефония", icon: "ri-phone-line", category: t("Звонки"), hint: t("Входящие звонки и запись разговоров") },
];

/* ── роли ── */

export type RolePermission = { id: string; label: string; on: boolean };

export type WorkspaceRole = {
  id: string;
  name: string;
  icon: string;
  description: string;
  users: number;
  permissions: RolePermission[];
};

const rolePerms = (on: string[]): RolePermission[] =>
  [
    { id: "own", label: t("Свои диалоги") },
    { id: "all", label: t("Все диалоги") },
    { id: "contacts", label: t("Контакты") },
    { id: "broadcasts", label: t("Рассылки") },
    { id: "workflows", label: t("Рабочие процессы") },
    { id: "channels", label: t("Каналы") },
    { id: "settings", label: t("Настройки") },
    { id: "billing", label: t("Оплата и тариф") },
  ].map((p) => ({ ...p, on: on.includes(p.id) }));

export const rolesSeed: WorkspaceRole[] = [
  {
    id: "agent",
    name: t("Агент"),
    icon: "ri-user-line",
    description: t("Отвечает клиентам в назначенных диалогах и ведёт карточки контактов."),
    users: 4,
    permissions: rolePerms(["own", "contacts"]),
  },
  {
    id: "manager",
    name: t("Менеджер"),
    icon: "ri-user-star-line",
    description: t("Видит все диалоги, распределяет их по продавцам и смотрит отчёты."),
    users: 2,
    permissions: rolePerms(["own", "all", "contacts", "broadcasts", "workflows", "channels", "settings"]),
  },
  {
    id: "admin",
    name: t("Администратор"),
    icon: "ri-shield-user-line",
    description: t("Управляет каналами, участниками, интеграциями и оплатой."),
    users: 1,
    permissions: rolePerms(["own", "all", "contacts", "broadcasts", "workflows", "channels", "settings", "billing"]),
  },
];

/* ── приложения ── */

export type WorkspaceApp = {
  id: string;
  name: string;
  icon: string;
  category: string;
  description: string;
  enabled: boolean;
};

export const workspaceAppsSeed: WorkspaceApp[] = [
  {
    id: "ap-calendar",
    name: t("Календарь встреч"),
    icon: "ri-calendar-line",
    category: t("Продуктивность"),
    description: t("Свободные слоты в ленте диалога и ссылка на встречу в один клик."),
    enabled: true,
  },
  {
    id: "ap-tasks",
    name: t("Задачи"),
    icon: "ri-checkbox-multiple-line",
    category: t("Продуктивность"),
    description: t("Задача по диалогу с исполнителем и сроком, напоминание в чат."),
    enabled: true,
  },
  {
    id: "ap-forms",
    name: t("Формы и заявки"),
    icon: "ri-file-list-3-line",
    category: t("Продажи"),
    description: t("Форма на сайте создаёт контакт и первый диалог автоматически."),
    enabled: false,
  },
  {
    id: "ap-knowledge",
    name: t("База знаний"),
    icon: "ri-book-open-line",
    category: t("Поддержка"),
    description: t("Статьи для команды и источники знаний для AI Assist."),
    enabled: true,
  },
  {
    id: "ap-quality",
    name: t("Оценка качества"),
    icon: "ri-star-smile-line",
    category: t("Поддержка"),
    description: t("Оценка диалогов клиентом и разбор спорных ответов руководителем."),
    enabled: false,
  },
  {
    id: "ap-stock",
    name: t("Остатки и склад"),
    icon: "ri-stack-line",
    category: t("Продажи"),
    description: t("Актуальные остатки в карточке товара прямо во время переписки."),
    enabled: false,
  },
];

/* ── интеграции ── */

export type WorkspaceIntegration = {
  id: string;
  name: string;
  icon: string;
  description: string;
  connected: boolean;
  account: string;
};

export const integrationsSeed: WorkspaceIntegration[] = [
  {
    id: "in-amo",
    name: "amoCRM",
    icon: "ri-links-line",
    description: t("Контакты и сделки синхронизируются в обе стороны, этапы воронки совпадают."),
    connected: false,
    account: "",
  },
  {
    id: "in-bitrix",
    name: t("Битрикс24"),
    icon: "ri-building-2-line",
    description: t("Диалоги Hub создают лиды и задачи в Битрикс24 без ручного переноса."),
    connected: false,
    account: "",
  },
  {
    id: "in-sheets",
    name: t("Google Таблицы"),
    icon: "ri-table-line",
    description: t("Выгрузка контактов и отчётов в таблицу по расписанию."),
    connected: true,
    account: "sales@hub.ru",
  },
  {
    id: "in-1c",
    name: t("1С:Предприятие"),
    icon: "ri-database-2-line",
    description: t("Заказы, оплаты и остатки из 1С в карточке клиента и в ответах продавца."),
    connected: false,
    account: "",
  },
  {
    id: "in-zapier",
    name: "Zapier",
    icon: "ri-flashlight-line",
    description: t("Связка Hub с тысячами сервисов без разработки: события и действия по триггерам."),
    connected: false,
    account: "",
  },
  {
    id: "in-webhooks",
    name: t("Вебхуки и API"),
    icon: "ri-code-s-slash-line",
    description: t("Свой сервис получает события сообщений и может отправлять ответы в Hub."),
    connected: true,
    account: t("Ключ hub_live_7f31…"),
  },
];

export const settingsHints = [
  {
    icon: "ri-shield-check-line",
    title: t("Права по ролям"),
    text: t("Роль определяет доступ к каналам, настройкам и оплате — проверяйте её перед приглашением."),
  },
  {
    icon: "ri-user-shared-line",
    title: t("Команды вместо людей"),
    text: t("Назначайте диалоги и контакты целой команде, а не каждому сотруднику по отдельности."),
  },
  {
    icon: "ri-refresh-line",
    title: t("Изменения сразу"),
    text: t("Настройки применяются к рабочему пространству сразу после сохранения — перезапуск не нужен."),
  },
];